import { fetchMockSalesData, fetchFilteredAdvertisingData } from "../shared/mockData";
import {
  getAdvertisingFilterStateForSheet,
  filterStateManager,
  initializeFilterStateManager,
} from "../shared/filterStateManager";
import { STORAGE_KEYS, SHEET_PATTERNS, EXCEL_DATA } from "../shared/constants";

/**
 * Write large 2D arrays in smaller batches to avoid Excel web payload limits.
 * @param startColumn The starting column number (1-based). Defaults to 1 (column A).
 */
async function writeValuesInChunks(
  sheet: Excel.Worksheet,
  startRow: number,
  columnsLength: number,
  data: any[][],
  context: Excel.RequestContext,
  chunkSize: number = EXCEL_DATA.CHUNK_SIZE,
  startColumn: number = 1
) {
  const startColLetter = getExcelColumnLetter(startColumn);
  const endColLetter = getExcelColumnLetter(startColumn + columnsLength - 1);
  for (let i = 0; i < data.length; i += chunkSize) {
    const chunk = data.slice(i, i + chunkSize);
    const from = startRow + i;
    const to = from + chunk.length - 1;
    const range = sheet.getRange(`${startColLetter}${from}:${endColLetter}${to}`);
    range.values = chunk;
    await context.sync();
  }
}

/**
 * Creates a dashboard-style heading at the top of a worksheet with 3 rows, copying Sales Dashboard header style.
 * Shared by Raw Data / Sales Data / Advertising Raw Data sheets.
 */
async function createDashboardHeading(
  sheet: Excel.Worksheet,
  headingText: string,
  context: Excel.RequestContext,
  dashboardType: string = "Raw Data",
  _dateRange?: string,
  startPurchaseDate?: string,
  endPurchaseDate?: string
) {
  // Helper function to format date for display (copied from Sales Dashboard)
  const formatDateForDisplay = (dateStr: string) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // ============ TITLE + INLINE DATE INPUTS SECTION ============
  // Header background band (extend across entire title area)
  const headerBandRange = sheet.getRange("A1:K2");
  headerBandRange.format.fill.color = "#F0F4F8";
  headerBandRange.format.verticalAlignment = "Center";

  // Title area - adapted for Data sheets; Advertising Raw Data should start at A1
  const isAdvertisingHeading = headingText === "Advertising Raw Data";
  const titleAddress = isAdvertisingHeading ? "A1:D2" : "C1:F2";
  const titleCell = isAdvertisingHeading ? "A1" : "C1";
  const titleTextRange = sheet.getRange(titleAddress);
  titleTextRange.merge();

  // Replace "Dashboard" with "Data" in the heading
  let displayHeading = headingText;
  if (headingText.includes("Dashboard")) {
    displayHeading = headingText.replace("Dashboard", "Data");
  } else if (!headingText.includes("Data")) {
    displayHeading = `${headingText} Data`;
  }

  sheet.getRange(titleCell).values = [[displayHeading]];
  titleTextRange.format.font.size = 16;
  titleTextRange.format.font.bold = true;
  titleTextRange.format.font.color = "#1F4788";
  titleTextRange.format.horizontalAlignment = "Left";
  titleTextRange.format.fill.color = "#F0F4F8";

  // Nudge title for non-Advertising only; Advertising should start at A with no indent
  try {
    if (!isAdvertisingHeading) {
      (titleTextRange.format as any).indentLevel = 1;
    }
  } catch (e) {
    /* not critical */
  }

  // ============ THIRD ROW FOR FILTER INFORMATION ============
  const thirdRowRange = sheet.getRange("A3:K3");
  thirdRowRange.format.fill.color = "#F0F4F8";

  // Show editable date inputs ONLY for dashboard-style sheets, not for any "Raw Data"/"Data" sheets
  const isDataLikeHeading = /Raw Data|Data/i.test(headingText);
  if (dashboardType !== "Raw Data" && !isDataLikeHeading) {
    // Inline date inputs moved to third row: Start (I3), hyphen (J3), End (K3)
    const startDateCell = sheet.getRange("I3");
    const hyphenCell = sheet.getRange("J3");
    const endDateCell = sheet.getRange("K3");

    // Check if we have date range data to display
    const shouldShowDateRange = startPurchaseDate && endPurchaseDate;

    // Write incoming dates to the input cells (display format only)
    const displayStartDate = shouldShowDateRange ? formatDateForDisplay(startPurchaseDate) : "";
    const displayEndDate = shouldShowDateRange ? formatDateForDisplay(endPurchaseDate) : "";

    console.log("Setting Data sheet date cells with:", {
      displayStartDate,
      displayEndDate,
      startPurchaseDate,
      endPurchaseDate,
    });

    startDateCell.values = [[displayStartDate]];
    endDateCell.values = [[displayEndDate]];
    hyphenCell.values = [["-"]];

    // Styling for minimal, sleek inputs
    [startDateCell, endDateCell].forEach((cell) => {
      cell.format.font.size = 11;
      cell.format.font.color = "#1F4788";
      cell.format.horizontalAlignment = "Left";
      cell.format.verticalAlignment = "Center";
      cell.numberFormat = [["mmm d, yyyy"]];
      // Minimal underline style to suggest input without heavy borders
      const bottomBorder = cell.format.borders.getItem("EdgeBottom");
      bottomBorder.style = "Continuous";
      bottomBorder.color = "#C5D1E6";
      bottomBorder.weight = Excel.BorderWeight.hairline;
      // Clear the other edges to stay borderless
      ["EdgeTop", "EdgeLeft", "EdgeRight"].forEach((edge) => {
        const b = cell.format.borders.getItem(edge as Excel.BorderIndex);
        b.weight = Excel.BorderWeight.hairline;
        b.color = "#F0F4F8";
      });
      cell.format.fill.color = "#F0F4F8";
    });

    hyphenCell.format.font.size = 12;
    hyphenCell.format.font.color = "#1F4788";
    hyphenCell.format.horizontalAlignment = "Center";
    hyphenCell.format.verticalAlignment = "Center";
    hyphenCell.format.fill.color = "#F0F4F8";

    // Set appropriate column widths for date input columns
    try {
      sheet.getRange("I:I").format.columnWidth = 90; // Start date column
      sheet.getRange("J:J").format.columnWidth = 30; // Hyphen column
      sheet.getRange("K:K").format.columnWidth = 90; // End date column
    } catch (e) {
      console.warn("Could not set date column widths:", e);
    }
  }

  // Handle SKU filtering display for Sales and Inventory types
  if (dashboardType === "Sales" || dashboardType === "Inventory") {
    // Get filter parameters from storage
    const filterParams = await OfficeRuntime.storage.getItem(STORAGE_KEYS.DASH_FILTER);

    if (filterParams) {
      const params = JSON.parse(filterParams);

      // Display filtered SKUs centered below the dashboard heading (B3:F3)
      if (params.skus && params.skus.length > 0) {
        const skuText = params.skus.join(", ");
        const filterText = `Filtered SKUs: ${skuText}`;

        const centeredSkuRange = sheet.getRange("B3:F3");
        centeredSkuRange.merge();
        sheet.getRange("B3").values = [[filterText]];
        centeredSkuRange.format.font.size = 10;
        centeredSkuRange.format.font.italic = true;
        centeredSkuRange.format.font.color = "#666666";
        centeredSkuRange.format.horizontalAlignment = "Center";
        centeredSkuRange.format.verticalAlignment = "Center";
        centeredSkuRange.format.fill.color = "#F0F4F8";
      } else {
        // Clear the cells if no SKUs are filtered
        try {
          sheet.getRange("B3:F3").unmerge();
        } catch (e) {
          /* ignore if not merged */
        }
        sheet.getRange("B3:F3").clear(Excel.ClearApplyTo.contents);
      }
    }
  }

  await context.sync();
}

async function findFilterParams() {
  const filterParams = await OfficeRuntime.storage.getItem(STORAGE_KEYS.DASH_FILTER);
  if (filterParams) {
    const params = JSON.parse(filterParams);
    return params;
  }
  return null;
}

/**
 * Converts formatted date range to ISO date parameters
 * @param dateRange Formatted date range string like "Dec 16, 2024 - Jan 15, 2025"
 * @returns Object with start_purchase_date and end_purchase_date in ISO format
 */
function convertDateRangeToParams(
  dateRange: string | null
): { start_purchase_date: string; end_purchase_date: string } | null {
  if (!dateRange) return null;

  try {
    // Split the date range by " - "
    const parts = dateRange.split(" - ");
    if (parts.length !== 2) return null;

    const startDate = new Date(parts[0].trim());
    const endDate = new Date(parts[1].trim());

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      return null;
    }

    return {
      start_purchase_date: startDate.toISOString(),
      end_purchase_date: endDate.toISOString(),
    };
  } catch (error) {
    console.error("Error converting date range to params:", error);
    return null;
  }
}

/**
 * Determines if a sheet is a Raw Data type sheet based on its name
 * @param sheetName The name of the sheet to check
 * @returns true if the sheet is a Raw Data type sheet
 */
export function isRawDataSheet(sheetName: string): boolean {
  // Check for exact "Raw Data" match
  if (sheetName === "Raw Data") {
    return true;
  }

  // Check for contextual raw data patterns
  const rawDataPatterns = [
    SHEET_PATTERNS.SALES_RAW_DATA,
    SHEET_PATTERNS.INVENTORY_RAW_DATA,
    SHEET_PATTERNS.ADVERTISING_RAW_DATA,
    SHEET_PATTERNS.SALES_DATA, // Legacy support
  ];

  return rawDataPatterns.some((pattern) => pattern.test(sheetName));
}

/**
 * Determines if a sheet is specifically a Sales Data sheet (orders only)
 * @param sheetName The name of the sheet to check
 * @returns true if the sheet is a Sales Data sheet
 */
export function isSalesDataSheet(sheetName: string): boolean {
  // Check for Sales Data patterns (both legacy and contextual naming)
  const salesDataPatterns = [
    SHEET_PATTERNS.SALES_RAW_DATA,
    SHEET_PATTERNS.SALES_DATA, // Legacy support
  ];

  return salesDataPatterns.some((pattern) => pattern.test(sheetName));
}

export function isAdvertisingDataSheet(sheetName: string): boolean {
  // Check for Advertising Data patterns (both legacy and contextual naming)
  const advertisingDataPatterns = [SHEET_PATTERNS.ADVERTISING_RAW_DATA_NUMBERED];

  return advertisingDataPatterns.some((pattern) => pattern.test(sheetName));
}

/**
 * Determines if a sheet is specifically a Raw Data sheet (products + orders)
 * @param sheetName The name of the sheet to check
 * @returns true if the sheet is a pure Raw Data sheet
 */
export function isPureRawDataSheet(sheetName: string): boolean {
  // Check for exact "Raw Data" match
  return sheetName === "Raw Data";
}

export async function generateSalesDashboard(
  context: Excel.RequestContext,
  dataSheet: Excel.Worksheet | null = null,
  dateRange?: string,
  customSheetName?: string
) {
  let rawDataSheet: Excel.Worksheet = dataSheet;
  let sheetName = customSheetName || "Sales Data";

  if (!dataSheet) {
    const worksheets = context.workbook.worksheets;
    worksheets.load("items/name");
    await context.sync();
    rawDataSheet = worksheets.add(sheetName);
    rawDataSheet.activate();
  } else {
    // Get the actual sheet name
    dataSheet.load("name");
    await context.sync();
    sheetName = dataSheet.name;
  }
  await context.sync();

  // Get filter params and merge with date range if provided
  let params = await findFilterParams();
  const dateParams = convertDateRangeToParams(dateRange);

  if (dateParams) {
    // If we have date range from Dashboard, use it
    params = {
      ...params,
      start_purchase_date: dateParams.start_purchase_date,
      end_purchase_date: dateParams.end_purchase_date,
      skus: params?.skus || [],
    };
  }

  // Add dashboard-style heading using the actual sheet name with ISO dates for editable inputs
  await createDashboardHeading(
    rawDataSheet,
    sheetName,
    context,
    "Sales",
    dateRange,
    params?.start_purchase_date,
    params?.end_purchase_date
  );

  console.log("params with date range", params);
  const salesDataResponse = await fetchMockSalesData(params);

  // Use raw columnar data from API only
  if (!salesDataResponse.columns || !salesDataResponse.data) {
    throw new Error("Invalid API response: missing columns or data");
  }

  const columns = salesDataResponse.columns;
  const dataRows = salesDataResponse.data;

  const orderStartRow = 5;
  const lastCol = getExcelColumnLetter(columns.length);

  // Set headers from API response
  const orderHeaders = [columns];
  const orderHeaderRange = rawDataSheet.getRange(`A${orderStartRow}:${lastCol}${orderStartRow}`);
  orderHeaderRange.values = orderHeaders;
  orderHeaderRange.format.fill.color = "#4472C4";
  orderHeaderRange.format.font.color = "white";
  orderHeaderRange.format.font.bold = true;
  await context.sync();

  if (dataRows.length > 0) {
    await writeValuesInChunks(
      rawDataSheet,
      orderStartRow + 1,
      columns.length,
      dataRows,
      context
    );

    // Format currency columns if they exist
    const salePriceIndex = columns.indexOf("sale_price");
    if (salePriceIndex >= 0) {
      const salePriceCol = getExcelColumnLetter(salePriceIndex + 1);
      const amountRange = rawDataSheet.getRange(
        `${salePriceCol}${orderStartRow + 1}:${salePriceCol}${orderStartRow + dataRows.length}`
      );
      amountRange.numberFormat = [["$#,##0.00"]];
    }

    // Create Excel table with the data
    const tableRange = rawDataSheet.getRange(
      `A${orderStartRow}:${lastCol}${orderStartRow + dataRows.length}`
    );
    const table = rawDataSheet.tables.add(tableRange, true);
    table.name = "SalesDataTable";
    table.style = "TableStyleMedium2";
    await context.sync();

    // Auto-fit columns
    rawDataSheet
      .getRange(`A${orderStartRow}:${lastCol}${orderStartRow + dataRows.length}`)
      .format.autofitColumns();
    await context.sync();
  }

  await context.sync();
}

export async function updateCopiedSheet(
  context: Excel.RequestContext,
  copiedSheet: Excel.Worksheet,
  dateRange?: string
) {
  // Get the actual sheet name
  copiedSheet.load("name");
  await context.sync();
  const sheetName = copiedSheet.name;

  // Get filter params and merge with date range if provided
  let params = await findFilterParams();
  const dateParams = convertDateRangeToParams(dateRange);

  if (dateParams) {
    params = {
      ...params,
      start_purchase_date: dateParams.start_purchase_date,
      end_purchase_date: dateParams.end_purchase_date,
      skus: params?.skus || [],
    };
  }

  await createDashboardHeading(
    copiedSheet,
    sheetName,
    context,
    "Sales",
    dateRange,
    params?.start_purchase_date,
    params?.end_purchase_date
  );

  console.log("params in copied with date range", params);
  const salesDataResponse = await fetchMockSalesData(params);

  if (!salesDataResponse.columns || !salesDataResponse.data) {
    throw new Error("Invalid API response: missing columns or data");
  }

  const columns = salesDataResponse.columns;
  const dataRows = salesDataResponse.data;

  const orderStartRow = 5;
  const lastCol = getExcelColumnLetter(columns.length);

  // Set headers from API response
  const orderHeaders = [columns];
  const orderHeaderRange = copiedSheet.getRange(`A${orderStartRow}:${lastCol}${orderStartRow}`);
  orderHeaderRange.values = orderHeaders;
  orderHeaderRange.format.fill.color = "#4472C4";
  orderHeaderRange.format.font.color = "white";
  orderHeaderRange.format.font.bold = true;

  if (dataRows.length > 0) {
    await writeValuesInChunks(
      copiedSheet,
      orderStartRow + 1,
      columns.length,
      dataRows,
      context
    );

    // Format currency columns if they exist
    const salePriceIndex = columns.indexOf("sale_price");
    if (salePriceIndex >= 0) {
      const salePriceCol = getExcelColumnLetter(salePriceIndex + 1);
      const amountRange = copiedSheet.getRange(
        `${salePriceCol}${orderStartRow + 1}:${salePriceCol}${orderStartRow + dataRows.length}`
      );
      amountRange.numberFormat = [["$#,##0.00"]];
    }

    // Create Excel table with the data
    const tableRange = copiedSheet.getRange(
      `A${orderStartRow}:${lastCol}${orderStartRow + dataRows.length}`
    );
    const table = copiedSheet.tables.add(tableRange, true);
    table.name = "SalesDataTable";
    table.style = "TableStyleMedium2";
    await context.sync();

    // Auto-fit columns
    copiedSheet
      .getRange(`A${orderStartRow}:${lastCol}${orderStartRow + dataRows.length}`)
      .format.autofitColumns();
    await context.sync();
  }

  const validRows = orderStartRow + dataRows.length;
  const usedRange = copiedSheet.getUsedRange();
  usedRange.load("rowCount");
  usedRange.load("columnCount");
  await context.sync();

  const totalRows = usedRange.rowCount;

  if (validRows < totalRows) {
    const clearRange = copiedSheet.getRangeByIndexes(
      validRows,
      0,
      totalRows - validRows,
      usedRange.columnCount
    );
    clearRange.clear();
  }

  await context.sync();
}

/**
 * Refreshes the Sales Data sheet with only orders data (matching generateSalesDashboard layout)
 * @param context Excel context
 * @param salesDataSheet The existing Sales Data sheet to refresh
 * @param dateRange Optional date range to display in the header
 */
export async function refreshSalesDataSheet(
  context: Excel.RequestContext,
  salesDataSheet: Excel.Worksheet,
  dateRange?: string
) {
  // Clear the existing content (keeping the sheet)
  salesDataSheet.getUsedRange()?.clear(Excel.ClearApplyTo.all);
  await context.sync();

  // Get the actual sheet name
  salesDataSheet.load("name");
  await context.sync();
  const sheetName = salesDataSheet.name;

  // Get filter params and merge with date range if provided
  let params = await findFilterParams();
  const dateParams = convertDateRangeToParams(dateRange);

  if (dateParams) {
    params = {
      ...params,
      start_purchase_date: dateParams.start_purchase_date,
      end_purchase_date: dateParams.end_purchase_date,
      skus: params?.skus || [],
    };
  }

  await createDashboardHeading(
    salesDataSheet,
    sheetName,
    context,
    "Sales",
    dateRange,
    params?.start_purchase_date,
    params?.end_purchase_date
  );

  console.log("refreshSalesDataSheet params with date range", params);
  const salesDataResponse = await fetchMockSalesData(params);

  // Use raw columnar data from API only
  if (!salesDataResponse.columns || !salesDataResponse.data) {
    throw new Error("Invalid API response: missing columns or data");
  }

  const columns = salesDataResponse.columns;
  const dataRows = salesDataResponse.data;

  const orderStartRow = 5;
  const lastCol = getExcelColumnLetter(columns.length);

  // Set headers from API response
  const orderHeaders = [columns];
  const orderHeaderRange = salesDataSheet.getRange(`A${orderStartRow}:${lastCol}${orderStartRow}`);
  orderHeaderRange.values = orderHeaders;
  orderHeaderRange.format.fill.color = "#4472C4";
  orderHeaderRange.format.font.color = "white";
  orderHeaderRange.format.font.bold = true;

  if (dataRows.length > 0) {
    await writeValuesInChunks(
      salesDataSheet,
      orderStartRow + 1,
      columns.length,
      dataRows,
      context
    );

    // Format currency columns if they exist
    const salePriceIndex = columns.indexOf("sale_price");
    if (salePriceIndex >= 0) {
      const salePriceCol = getExcelColumnLetter(salePriceIndex + 1);
      const amountRange = salesDataSheet.getRange(
        `${salePriceCol}${orderStartRow + 1}:${salePriceCol}${orderStartRow + dataRows.length}`
      );
      amountRange.numberFormat = [["$#,##0.00"]];
    }

    // Create Excel table with the data
    const tableRange = salesDataSheet.getRange(
      `A${orderStartRow}:${lastCol}${orderStartRow + dataRows.length}`
    );
    const table = salesDataSheet.tables.add(tableRange, true);
    table.name = "SalesDataTable";
    table.style = "TableStyleMedium2";
    await context.sync();

    // Auto-fit columns
    salesDataSheet
      .getRange(`A${orderStartRow}:${lastCol}${orderStartRow + dataRows.length}`)
      .format.autofitColumns();
    await context.sync();
  }

  await context.sync();
}

/**
 * Refreshes the Raw Data sheet with both products and orders data
 * @param context Excel context
 * @param rawDataSheet The existing Raw Data sheet to refresh
 * @param dateRange Optional date range to display in the header
 */
export async function refreshRawDataSheet(
  context: Excel.RequestContext,
  rawDataSheet: Excel.Worksheet,
  dateRange?: string
) {
  // Clear the existing content (keeping the sheet)
  rawDataSheet.getUsedRange()?.clear(Excel.ClearApplyTo.all);
  await context.sync();

  // Get the actual sheet name
  rawDataSheet.load("name");
  await context.sync();
  const sheetName = rawDataSheet.name;

  // Fetch and display raw data
  const { fetchMockSalesData, fetchMockProductData } = await import("../shared/mockData");

  // Apply date range filtering if provided
  let params = null;
  const dateParams = convertDateRangeToParams(dateRange);
  if (dateParams) {
    params = {
      start_purchase_date: dateParams.start_purchase_date,
      end_purchase_date: dateParams.end_purchase_date,
      skus: [],
    };
  }

  // Add dashboard-style heading using actual sheet name with ISO dates for editable inputs
  await createDashboardHeading(
    rawDataSheet,
    sheetName,
    context,
    "Raw Data",
    dateRange,
    params?.start_purchase_date,
    params?.end_purchase_date
  );

  console.log("refreshRawDataSheet params with date range", params);
  const salesDataResponse = await fetchMockSalesData(params);
  const productData = await fetchMockProductData();

  const productHeaders = [["SKU", "ASIN", "Product Name", "Category", "Cost per unit"]];
  const productHeaderRange = rawDataSheet.getRange("A4:E4");
  productHeaderRange.values = productHeaders;
  productHeaderRange.format.fill.color = "#4472C4";
  productHeaderRange.format.font.color = "white";
  productHeaderRange.format.font.bold = true;

  // Prepare product data rows
  const productRows = productData.products.map((product) => {
    const { SKU, ASIN, Name, Category, ["Cost per unit"]: costPerUnit } = product;
    return [SKU || "", ASIN || "", Name || "", Category || "", costPerUnit || ""];
  });

  if (productRows.length > 0) {
    await writeValuesInChunks(
      rawDataSheet,
      5,
      5,
      productRows,
      context
    );

    // Format cost per unit column as currency
    const costRange = rawDataSheet.getRange(`E5:E${4 + productRows.length}`);
    costRange.numberFormat = [["$#,##0.00"]];
  }

  if (!salesDataResponse.columns || !salesDataResponse.data) {
    throw new Error("Invalid API response: missing columns or data");
  }

  const columns = salesDataResponse.columns;
  const dataRows = salesDataResponse.data;

  const orderStartRow = 4 + productRows.length + 3;
  const lastCol = getExcelColumnLetter(columns.length);

  // Add order section header
  const orderSectionHeader = rawDataSheet.getRange(
    `A${orderStartRow - 1}:${lastCol}${orderStartRow - 1}`
  );
  orderSectionHeader.merge();
  rawDataSheet.getRange(`A${orderStartRow - 1}`).values = [["Orders Data"]];
  orderSectionHeader.format.font.size = 14;
  orderSectionHeader.format.font.bold = true;
  orderSectionHeader.format.font.color = "#1F4788";
  orderSectionHeader.format.horizontalAlignment = "Center";
  orderSectionHeader.format.fill.color = "#E8F0FE";

  // Add order headers from API response
  const orderHeaders = [columns];
  const orderHeaderRange = rawDataSheet.getRange(`A${orderStartRow}:${lastCol}${orderStartRow}`);
  orderHeaderRange.values = orderHeaders;
  orderHeaderRange.format.fill.color = "#4472C4";
  orderHeaderRange.format.font.color = "white";
  orderHeaderRange.format.font.bold = true;

  if (dataRows.length > 0) {
    await writeValuesInChunks(
      rawDataSheet,
      orderStartRow + 1,
      columns.length,
      dataRows,
      context
    );

    // Format currency columns if they exist
    const salePriceIndex = columns.indexOf("sale_price");
    if (salePriceIndex >= 0) {
      const salePriceCol = getExcelColumnLetter(salePriceIndex + 1);
      const amountRange = rawDataSheet.getRange(
        `${salePriceCol}${orderStartRow + 1}:${salePriceCol}${orderStartRow + dataRows.length}`
      );
      amountRange.numberFormat = [["$#,##0.00"]];
    }

    // Create Excel table for orders data
    const orderTableRange = rawDataSheet.getRange(
      `A${orderStartRow}:${lastCol}${orderStartRow + dataRows.length}`
    );
    const orderTable = rawDataSheet.tables.add(orderTableRange, true);
    orderTable.name = "OrdersDataTable";
    orderTable.style = "TableStyleMedium2";
    await context.sync();
  }

  // Auto-fit all columns
  rawDataSheet.getUsedRange()?.format.autofitColumns();
  await context.sync();
}

function getExcelColumnLetter(columnNumber: number): string {
  let n = Math.max(1, Math.floor(columnNumber));
  let columnName = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    columnName = String.fromCharCode(65 + rem) + columnName;
    n = Math.floor((n - 1) / 26);
  }
  return columnName;
}

/**
 * Refreshes the Advertising Raw Data sheet with filtered data based on sheet-level custom properties
 */
export async function refreshAdvertisingRawDataSheet(
  context: Excel.RequestContext,
  advertisingSheet: Excel.Worksheet,
  preserve: boolean = false
) {
  // Check for existing headers and data BEFORE clearing
  const usedRangeBefore = advertisingSheet.getUsedRange();
  let existingHeadersToPreserve: string[] = [];
  let existingDataToPreserve: any[][] = [];
  let existingDataRowCount = 0;



  if (usedRangeBefore && preserve) {
    usedRangeBefore.load("columnCount,rowCount");
    await context.sync();

    if (usedRangeBefore.columnCount > 0) {
      console.log("CALL INSIDE IF")
      // Read all existing headers from row 4
      const allExistingHeadersRange = advertisingSheet.getRange(
        `A4:${getExcelColumnLetter(usedRangeBefore.columnCount)}4`
      );
      allExistingHeadersRange.load("values");
      await context.sync();
      const allExistingHeaders = (allExistingHeadersRange.values[0] || []) as any[];

      // Store headers temporarily (we'll filter after we know the new columns)
      const tempHeaders = allExistingHeaders.map((h: any) => h?.toString().trim() || "").filter((h: string) => h !== "");
      
      // Store data temporarily
      existingDataRowCount = Math.max(0, usedRangeBefore.rowCount - 4);
      if (existingDataRowCount > 0 && tempHeaders.length > 0) {
        const allExistingDataRange = advertisingSheet.getRange(
          `A5:${getExcelColumnLetter(usedRangeBefore.columnCount)}${4 + existingDataRowCount}`
        );
        allExistingDataRange.load("values");
        await context.sync();
        existingDataToPreserve = allExistingDataRange.values as any[][];
      }
      
      // Store all headers for later filtering
      existingHeadersToPreserve = tempHeaders;
    }
  }

  console.log("existingDataToPreserve", existingDataToPreserve);
  console.log("existingHeadersToPreserve", existingHeadersToPreserve);
  console.log("existingDataRowCount", existingDataRowCount);

  // Clear the existing content (keeping the sheet)
  advertisingSheet.getUsedRange()?.clear(Excel.ClearApplyTo.all);
  await context.sync();

  // Add header band/title
  await createDashboardHeading(
    advertisingSheet,
    "Advertising Raw Data",
    context,
    "Raw Data",
    undefined,
    undefined,
    undefined
  );

  let filtersForApi: any = {};
  let appliedSummary: string = "";

  // Get sheet name for per-sheet filter lookup
  advertisingSheet.load("name");
  await context.sync();
  const sheetName = advertisingSheet.name;

  try {
    if (!filterStateManager.isInitialized()) {
      await initializeFilterStateManager();
    }
    const p = await filterStateManager.hydrateAdvertisingFiltersForSheet(sheetName);

    if (p.startDate && p.endDate) {
      const start = typeof p.startDate === "string" ? p.startDate : p.startDate?.toISOString().split("T")[0];
      const end = typeof p.endDate === "string" ? p.endDate : p.endDate?.toISOString().split("T")[0];
      if (start && end) {
        filtersForApi.startDate = start;
        filtersForApi.endDate = end;
      }
    }
    if (p.brand && p.brand.length > 0) filtersForApi.brands = p.brand;
    if (p.skus && p.skus.length > 0) filtersForApi.asins = p.skus;
    if (p.channel && p.channel !== "all") filtersForApi.channels = [p.channel];
    if (p.keyword && p.keyword.length > 0) filtersForApi.keyword_contains = p.keyword;
    if (p.campaign && p.campaign.length > 0) filtersForApi.campaign_name = p.campaign;

    // Build applied filter summary for header row 3 (B3:F3)
    const formatDateMMDDYYYY = (dateStr: string) => {
      const date = new Date(dateStr);
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    };
    const parts: string[] = [];
    if (p.keyword && p.keyword.length > 0)
      parts.push(`Keyword: ${Array.isArray(p.keyword) ? p.keyword.join(", ") : p.keyword}`);
    if (p.campaign && p.campaign.length > 0) parts.push(`Campaign: ${p.campaign.join(", ")}`);
    if (p.brand && p.brand.length > 0) parts.push(`Brand: ${p.brand.join(", ")}`);
    if (p.skus && p.skus.length > 0) parts.push(`ASIN/SKU: ${p.skus.join(", ")}`);
    if (p.startDate && p.endDate) {
      const startDate = formatDateMMDDYYYY(
        typeof p.startDate === "string" ? p.startDate : p.startDate?.toISOString()
      );
      const endDate = formatDateMMDDYYYY(
        typeof p.endDate === "string" ? p.endDate : p.endDate?.toISOString()
      );
      parts.push(`Dates: ${startDate} - ${endDate}`);
    }
    appliedSummary = parts.length > 0 ? `Filters: ${parts.join(" • ")}` : "";
  } catch (error) {
    console.error("Error loading filters for sheet:", error);
  }
  const resp = await fetchFilteredAdvertisingData(filtersForApi);

  const columns: string[] = resp?.columns || [];
  const data: any[][] = resp?.data || [];

  if (columns.length === 0) {
    return;
  }

  const lastCol = getExcelColumnLetter(columns.length);

  try {
    const r = advertisingSheet.getRange("A3:K3");
    try {
      r.unmerge();
    } catch (_) {}
    r.merge();
    advertisingSheet.getRange("A3").values = [[appliedSummary]];
    r.format.font.size = 10;
    r.format.font.italic = true as any;
    r.format.font.color = "#666666";
    // Left-align so it visually starts from A
    r.format.horizontalAlignment = "Left";
    r.format.verticalAlignment = "Center";
    r.format.fill.color = "#F0F4F8";
  } catch (_) {}

  // Filter existing headers to only keep those NOT in new columns
  const headersToPreserve: string[] = [];
  const preserveColumnIndices: number[] = [];
  
  if (existingHeadersToPreserve.length > 0) {
    existingHeadersToPreserve.forEach((header: string, index: number) => {
      if (header && !columns.includes(header)) {
        headersToPreserve.push(header);
        preserveColumnIndices.push(index);
      }
    });
  }

  // Extract data for preserved columns only
  const dataToPreserve: any[][] = [];
  if (headersToPreserve.length > 0 && existingDataToPreserve.length > 0) {
    existingDataToPreserve.forEach((row: any[]) => {
      const preservedRow: any[] = [];
      preserveColumnIndices.forEach((colIndex) => {
        // Preserve 0 values by checking for undefined/null explicitly
        const value = row?.[colIndex];
        preservedRow.push(value !== undefined && value !== null ? value : null);
      });
      if (preservedRow.length > 0) {
        dataToPreserve.push(preservedRow);
      }
    });
  }

  // Headers - write new headers
  const headerRange = advertisingSheet.getRange(`A4:${lastCol}4`);
  headerRange.values = [columns];
  headerRange.format.fill.color = "#4472C4";
  headerRange.format.font.color = "white";
  headerRange.format.font.bold = true;

  // Preserve existing headers and data that are not in the new columns
  if (headersToPreserve.length > 0) {
    const preserveStartCol = getExcelColumnLetter(columns.length + 1);
    const preserveEndCol = getExcelColumnLetter(columns.length + headersToPreserve.length);

    // Preserve headers
    const preserveHeaderRange = advertisingSheet.getRange(
      `${preserveStartCol}4:${preserveEndCol}4`
    );
    preserveHeaderRange.values = [headersToPreserve];
    preserveHeaderRange.format.fill.color = "#4472C4";
    preserveHeaderRange.format.font.color = "white";
    preserveHeaderRange.format.font.bold = true;

    // Preserve data
    if (dataToPreserve.length > 0) {
      console.log("CALL INSIDE IF 2")
      // Write preserved data starting after the new columns
      const preserveStartColumn = columns.length + 1;
      await writeValuesInChunks(
        advertisingSheet,
        5,
        headersToPreserve.length,
        dataToPreserve,
        context,
        500,
        preserveStartColumn
      );
    }
    await context.sync();
  }

  // Data
  if (data.length > 0) {
    await writeValuesInChunks(
      advertisingSheet,
      5,
      columns.length,
      data,
      context
    );
    advertisingSheet.getRange(`A4:${lastCol}${4 + data.length}`).format.autofitColumns();
    try {
      ["E", "F", "G", "H", "I", "J", "K"].forEach((col) => {
        advertisingSheet.getRange(`${col}:${col}`).format.columnWidth = 90;
      });
    } catch (_) {}
  }
}
